import type { FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { QuotaCycle } from '../../api.js';
import { cycleWindowStart, formatResetAt } from '../../utils/reset-at.js';

export type LimitSubjectKind = 'group' | 'user';

export interface LimitSubjectOption {
  id: string;
  label: string;
}

export interface CreditLimitFormState {
  subjectKind: LimitSubjectKind;
  subjectId: string;
  amount: string;
  cycle: QuotaCycle;
  applyToTenant: boolean;
  catalogModelId: string;
  overageBehavior: 'deny' | 'warn';
  /** Set while editing: the row being replaced, so a cycle/model change moves
   *  the limit instead of leaving a second one behind. */
  replaceId?: string;
}

const CYCLES: QuotaCycle[] = ['weekly', 'daily', 'monthly', 'lifetime'];

export function CreditLimitForm({
  state,
  subjects,
  models,
  busy,
  nextResetAt,
  locale,
  onChange,
  onSubmit,
  onCancelEdit,
  rateMissing = false,
  onSaveWithWarn
}: {
  state: CreditLimitFormState;
  subjects: LimitSubjectOption[];
  models: LimitSubjectOption[];
  busy: boolean;
  nextResetAt?: string;
  locale: string;
  onChange: (next: CreditLimitFormState) => void;
  onSubmit: (event: FormEvent) => void;
  onCancelEdit: () => void;
  /** The last attempt was rejected for a missing credit rate. */
  rateMissing?: boolean;
  onSaveWithWarn?: () => void;
}) {
  const { t } = useTranslation();
  const set = (patch: Partial<CreditLimitFormState>) => onChange({ ...state, ...patch });
  const localizedReset = formatResetAt(nextResetAt, locale);
  // The counter this limit is measured against is already running. Showing the
  // window's start is what makes `noCounterReset` actionable — an operator
  // setting a weekly cap on a Thursday is budgeting for four days, not seven.
  const localizedStart = formatResetAt(cycleWindowStart(state.cycle, nextResetAt), locale);
  const noSubjects = subjects.length === 0;

  return (
    <form className="panel credit-limit-form" onSubmit={onSubmit}>
      <h2>{state.replaceId ? t('creditLimits.editTitle') : t('creditLimits.addTitle')}</h2>
      <p className="muted">{t('creditLimits.notAGrant')}</p>

      <label>
        <span>{t('creditLimits.subject')}</span>
        <select
          value={state.subjectKind}
          onChange={(event) =>
            set({ subjectKind: event.target.value as LimitSubjectKind, subjectId: '' })
          }
          disabled={busy || state.applyToTenant}
        >
          <option value="group">{t('creditLimits.subjectGroup')}</option>
          <option value="user">{t('creditLimits.subjectUser')}</option>
        </select>
      </label>
      <label>
        <span>{t('creditLimits.subjectTarget')}</span>
        <select
          value={state.subjectId}
          onChange={(event) => set({ subjectId: event.target.value })}
          disabled={busy || state.applyToTenant || noSubjects}
          required={!state.applyToTenant}
        >
          <option value="">
            {t(noSubjects ? 'creditLimits.noSubjects' : 'creditLimits.selectSubject')}
          </option>
          {subjects.map((subject) => (
            <option key={subject.id} value={subject.id}>
              {subject.label}
            </option>
          ))}
        </select>
      </label>

      <label>
        <span>{t('creditLimits.amount')}</span>
        <input
          value={state.amount}
          inputMode="decimal"
          placeholder={t('creditLimits.amountPlaceholder')}
          onChange={(event) => set({ amount: event.target.value })}
          disabled={busy}
          autoComplete="off"
        />
      </label>

      <div className="credit-limit-cycle" role="group" aria-label={t('creditLimits.cycle')}>
        <span>{t('creditLimits.cycle')}</span>
        <div className="segmented-control">
          {CYCLES.map((cycle) => (
            <button
              key={cycle}
              type="button"
              className={state.cycle === cycle ? 'active' : ''}
              aria-pressed={state.cycle === cycle}
              onClick={() => set({ cycle })}
              disabled={busy}
            >
              {t(`creditLimits.cycle_${cycle}`)}
            </button>
          ))}
        </div>
      </div>
      <p className="muted" aria-live="polite">
        {state.cycle === 'weekly'
          ? t('creditLimits.weeklyReset', { localizedResetAt: localizedReset ?? '—' })
          : state.cycle === 'lifetime'
            ? t('creditLimits.lifetimeNoReset')
            : t('creditLimits.periodicReset', { localizedResetAt: localizedReset ?? '—' })}
        {localizedStart ? ` ${t('creditLimits.currentWindowStart', { localizedStartAt: localizedStart })}` : ''}
      </p>

      <details>
        <summary>{t('creditLimits.advanced')}</summary>
        <label className="checkbox-row">
          <input
            type="checkbox"
            checked={state.applyToTenant}
            onChange={(event) => set({ applyToTenant: event.target.checked })}
            disabled={busy}
          />
          <span>{t('creditLimits.applyToTenant')}</span>
        </label>
        <label>
          <span>{t('creditLimits.model')}</span>
          <select
            value={state.catalogModelId}
            onChange={(event) => set({ catalogModelId: event.target.value })}
            disabled={busy}
          >
            <option value="">{t('creditLimits.modelAll')}</option>
            {models.map((model) => (
              <option key={model.id} value={model.id}>
                {model.label}
              </option>
            ))}
          </select>
        </label>
        <p className="muted">{t('creditLimits.modelScopeNote')}</p>
        <div className="credit-limit-behavior" role="group" aria-label={t('creditLimits.behavior')}>
          <span>{t('creditLimits.behavior')}</span>
          <div className="segmented-control">
            {(['deny', 'warn'] as const).map((behavior) => (
              <button
                key={behavior}
                type="button"
                className={state.overageBehavior === behavior ? 'active' : ''}
                aria-pressed={state.overageBehavior === behavior}
                onClick={() => set({ overageBehavior: behavior })}
                disabled={busy}
              >
                {t(`creditLimits.behavior_${behavior}`)}
              </button>
            ))}
          </div>
        </div>
      </details>

      <p className="muted">{t('creditLimits.noCounterReset')}</p>
      {rateMissing && onSaveWithWarn ? (
        <p className="warning" role="alert">
          {t('creditLimits.saveWithWarnInsteadHint')}{' '}
          <button type="button" onClick={onSaveWithWarn} disabled={busy}>
            {t('creditLimits.saveWithWarnInstead')}
          </button>
        </p>
      ) : null}
      <div className="credit-limit-actions">
        <button disabled={busy || (!state.applyToTenant && !state.subjectId)}>
          {t('creditLimits.save')}
        </button>
        {state.replaceId ? (
          <button type="button" onClick={onCancelEdit} disabled={busy}>
            {t('creditLimits.cancelEdit')}
          </button>
        ) : null}
      </div>
    </form>
  );
}
